import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { Button, IconButton } from "@similarweb/ui-components";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { useCurrentStateAndParams } from "@uirouter/react";
import { PlainTooltip } from "app/components/React/Tooltip/PlainTooltip/PlainTooltip";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { useLoading } from "app/custom-hooks/loadingHook";
import { i18nFilter } from "app/filters/i18nFilter";
import { useCallback, useEffect, useRef } from "react";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { CenteredFlexColumn, FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import styled from "styled-components";
import * as utils from "app/components/filters-bar/utils";
import { allTrackers } from "app/services/track/track";

const fetchService = DefaultFetchService.getInstance();
const MULTI_KEYWORDS_ENDPOINT = "api/AutoCreateKeywordList/KeywordsList/Table/5";
const SINGLE_KEYWORD_ENDPOINT = "api/AutoCreateKeywordList/SingleKeyword/Table/3";
const HEADER_HEIGHT = 40;

interface IKeywordSuggestion {
    keyword: string;
    score: number;
}

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    > * {
        margin: 14px 0;
    }
`;

const SuggestedKeywordRow = styled.div<{ isChecked: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 42px;
    ${setFontVariant("input2", colorsPalettes.carbon[400])};

    > span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .SWReactIcons svg path {
        fill: ${(props) => (props.isChecked ? colorsPalettes.carbon[200] : colorsPalettes.blue[400])};
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: ${HEADER_HEIGHT}px;
`;

const HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    ${setFontVariant("subtitle2", colorsPalettes.carbon[500])};
    .SWReactIcons svg path {
        fill: ${colorsPalettes.carbon[400]};
    }

    > * {
        margin-right: 8px;
    }
`;

const CheckedItemIconWrapper = styled(FlexRow)`
    ${setFontVariant("button", colorsPalettes.blue[400])};
    .SWReactIcons svg path {
        fill: ${colorsPalettes.blue[400]};
    }
`;

const SuggestedKeywordsContainer = styled.div<{ isLoading: boolean }>`
    overflow-y: ${({ isLoading }) => (isLoading ? "hidden" : "unset")};
`;

const NoDataContainer = styled(CenteredFlexColumn)`
    height: 224px;
    width: 100%;
`;

const NoDataIllustrationWrapper = styled.div`
    width: 155px;
    margin-bottom: 16px;
    height: auto;
`;

const NoDataTitle = styled(I18n)`
    ${setFontVariant("subtitle1", colorsPalettes.carbon[500])};
`;

const NoDataSubtitle = styled(I18n)`
    ${setFontVariant("caption", colorsPalettes.carbon[300])};
`;

interface IKeywordsGroupEditorProps {
    seedKeywords: string[];
    numberOfElementsToAutoPopulate: number;
    isNewGroup: boolean;
    onAddKeyword: (keyword: string) => void;
    onAddMultipleKeywords: (keywords: string[]) => void;
    onFinishedLoading?: () => void;
}

export const KeywordsSuggestions = ({
    seedKeywords,
    numberOfElementsToAutoPopulate,
    isNewGroup,
    onAddMultipleKeywords,
    onAddKeyword,
    onFinishedLoading,
}: IKeywordsGroupEditorProps) => {
    const { params } = useCurrentStateAndParams();
    const [loadingState, ops, isLoading] = useLoading<{ keyword: string; score: number }[]>();
    const isFirstLoad = useRef(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = useCallback(() => {
        // no seed keywords yet (e.g. brand-new group) means "keys" would be empty and the API rejects it with a 400
        if (!seedKeywords.length) {
            ops.load(() => Promise.resolve([])).then(() => onFinishedLoading());
            return;
        }

        const isSingleKeyword = seedKeywords.length === 1 && isFirstLoad.current ? true : false;
        const endpoint = isSingleKeyword ? SINGLE_KEYWORD_ENDPOINT : MULTI_KEYWORDS_ENDPOINT;

        const { from, to, isWindow } = DurationService.getDurationData(BasicDurations.LAST_THREE_MONTHS).forAPI;

        const timeStart = performance.now();
        // TODO: implement top country, currently selects first country in list
        const topCountries = utils.getCountries();

        ops.load(() =>
            fetchService
                .get<IKeywordSuggestion[]>(endpoint, {
                    ...(isSingleKeyword ? { keyword: seedKeywords[0] } : { keys: seedKeywords.slice(-3).join(",") }), // take last 3 keywords
                    country: params.country || topCountries[0].id,
                    from,
                    to,
                    isWindow,
                    websource: params.webSource,
                    sort: "score",
                    asc: false,
                    rowsPerPage: 40,
                })
                .then((res) => {
                    const filteredSuggestions = res.filter((item) => !seedKeywords.includes(item.keyword));
                    // automatically add first N keywords
                    if (isNewGroup && isFirstLoad.current && filteredSuggestions.length) {
                        isFirstLoad.current = false;
                        onAddMultipleKeywords(
                            filteredSuggestions.slice(0, numberOfElementsToAutoPopulate).map((item) => item.keyword),
                        );
                    }

                    const timeEnd = performance.now();
                    allTrackers.dispatchTrackEvent(
                        "Table",
                        "table-results",
                        `Loading Time/${endpoint === SINGLE_KEYWORD_ENDPOINT ? "single" : "multi"}/${timeEnd - timeStart || ""}`,
                    );

                    onFinishedLoading();
                    // remove from list the keywords that should automatically be added
                    return filteredSuggestions.slice(numberOfElementsToAutoPopulate);
                }),
        );
    }, [seedKeywords]);

    const onAddKeywordClick = (keyword: string) => {
        onAddKeyword(keyword);
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Add suggested keyword");
    };

    const onAddAllKeywordsClick = () => {
        onAddMultipleKeywords(loadingState?.data?.map((item) => item.keyword));
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Create New Keyword Group/Add All Keywords");
    };

    const refreshResults = () => {
        fetchData();
        allTrackers.dispatchTrackEvent(
            "Button",
            "click",
            `${isNewGroup ? "Create New" : "Edit"} Keyword Group/Refresh`,
        );
    };

    return (
        <div>
            <Header>
                <HeaderTitle>
                    <SWReactIcons
                        iconName="wand"
                        size="sm"
                    />
                    <span>{i18nFilter()("KeywordAnalysis.keywordgroup.wizard.suggestions.title")}</span>
                </HeaderTitle>
                <div>
                    <PlainTooltip
                        placement="top"
                        text={i18nFilter()("KeywordAnalysis.keywordgroup.wizard.suggestions.refresh.tooltip")}
                    >
                        <div style={{ display: "inline-block" }}>
                            <IconButton
                                iconName="refresh"
                                type="flat"
                                showLoaderIcon={isLoading}
                                isDisabled={isLoading}
                                onClick={refreshResults}
                            />
                        </div>
                    </PlainTooltip>
                    <Button
                        isDisabled={!loadingState?.data?.length}
                        label={i18nFilter()("KeywordAnalysis.keywordgroup.wizard.suggestions.cta")}
                        type="flat"
                        onClick={onAddAllKeywordsClick}
                    />
                </div>
            </Header>
            <SuggestedKeywordsContainer isLoading={isLoading}>
                {isLoading ? (
                    <LoaderContainer>
                        {Array(8)
                            .fill(null)
                            .map((_, idx) => (
                                <PixelPlaceholderLoader
                                    key={idx}
                                    width={326}
                                    height={16}
                                />
                            ))}
                    </LoaderContainer>
                ) : loadingState?.data?.length ? (
                    loadingState.data.map((suggestion) => {
                        const isChecked = seedKeywords.includes(suggestion.keyword);
                        return (
                            <SuggestedKeywordRow
                                key={suggestion.keyword}
                                isChecked={isChecked}
                                className="list-item-text"
                            >
                                <span>{suggestion.keyword}</span>
                                {isChecked ? (
                                    <CheckedItemIconWrapper>
                                        <SWReactIcons
                                            iconName="checked"
                                            size="xs"
                                        />
                                        <I18n>KeywordAnalysis.keywordgroup.wizard.suggestions.item.added</I18n>
                                    </CheckedItemIconWrapper>
                                ) : (
                                    <IconButton
                                        dataAutomation="auto-create-suggestion-item-add"
                                        isDisabled={isChecked}
                                        iconName="add"
                                        type="flat"
                                        onClick={() => onAddKeywordClick(suggestion.keyword)}
                                    />
                                )}
                            </SuggestedKeywordRow>
                        );
                    })
                ) : (
                    <NoDataContainer>
                        <NoDataIllustrationWrapper>
                            <SWReactIcons iconName="no-data-tree" />
                        </NoDataIllustrationWrapper>
                        <NoDataTitle>KeywordAnalysis.keywordgroup.wizard.suggestions.no.data.title</NoDataTitle>
                        <NoDataSubtitle>
                            KeywordAnalysis.keywordgroup.wizard.suggestions.no.data.subtitle
                        </NoDataSubtitle>
                    </NoDataContainer>
                )}
            </SuggestedKeywordsContainer>
        </div>
    );
};
